<template>
  <v-menu location="bottom end" :close-on-content-click="true">
    <template #activator="{ props: menuProps }">
      <v-badge
        :model-value="showUnseenDot"
        color="tertiary"
        dot
        offset-x="6"
        offset-y="6"
      >
        <v-btn
          v-bind="menuProps"
          ref="helpButton"
          data-tour="page-help"
          class="page-help-button"
          icon
          variant="text"
          size="small"
          :aria-label="tooltipText"
        >
          <v-icon size="20">mdi-help-circle-outline</v-icon>
          <!-- `activator="parent"` keeps the menu's own activator binding intact;
               spreading tooltip props onto the button would override its ref. -->
          <v-tooltip activator="parent" location="bottom" :text="tooltipText" />
        </v-btn>
      </v-badge>
    </template>

    <v-list density="compact" min-width="280" class="page-help-button__menu">
      <v-list-subheader>{{ $t("ncrm.tours.launcher.menuTitle") }}</v-list-subheader>

      <v-list-item
        v-if="pageTourKey"
        prepend-icon="mdi-compass-outline"
        :title="$t('ncrm.tours.launcher.current')"
        :subtitle="$t('ncrm.tours.launcher.currentHint')"
        @click="startPageTour"
      >
        <template #append>
          <v-icon v-if="showUnseenDot" size="10" color="tertiary">mdi-circle</v-icon>
        </template>
      </v-list-item>

      <v-list-item
        prepend-icon="mdi-map-outline"
        :title="$t('ncrm.tours.launcher.full')"
        :subtitle="$t('ncrm.tours.launcher.fullHint')"
        @click="startFullTour"
      />

      <template v-if="progressUnavailable">
        <v-divider class="my-1" />
        <div class="page-help-button__notice">
          <v-icon size="14" class="page-help-button__notice-icon">mdi-alert-outline</v-icon>
          <span>{{ $t("ncrm.tours.launcher.progressUnavailable") }}</span>
        </div>
      </template>
    </v-list>
  </v-menu>
</template>

<script>
import { requestProductTour } from "@/Utile/tours/tourBus.mjs";
import {
  CRM_FULL_TOUR_KEY,
  CRM_TOUR_VERSION,
  getContextTourKeyForRoute,
} from "@/Utile/tours/tourDefinitions.mjs";
import { isTourUnseen, tourProgressState } from "@/Utile/tours/tourProgressStore.mjs";

export default {
  name: "PageHelpButton",
  props: {
    /**
     * Explicit page tour key. When omitted the tour is resolved from the
     * current route, so most pages only need `<PageHelpButton />`.
     */
    tourKey: {
      type: String,
      default: "",
    },
  },
  computed: {
    pageTourKey() {
      return this.tourKey || getContextTourKeyForRoute(this.$route?.path) || "";
    },
    showUnseenDot() {
      return Boolean(this.pageTourKey) && isTourUnseen(this.pageTourKey, CRM_TOUR_VERSION);
    },
    progressUnavailable() {
      return tourProgressState.failed;
    },
    tooltipText() {
      return this.$t("ncrm.tours.launcher.ariaLabel");
    },
  },
  methods: {
    returnFocusTarget() {
      return this.$refs.helpButton?.$el || null;
    },
    startPageTour() {
      if (!this.pageTourKey) return;
      requestProductTour({
        tourKey: this.pageTourKey,
        launchMode: "manual_section",
        returnFocusTo: this.returnFocusTarget(),
      });
    },
    startFullTour() {
      requestProductTour({
        tourKey: CRM_FULL_TOUR_KEY,
        launchMode: "manual_full",
        returnFocusTo: this.returnFocusTarget(),
      });
    },
  },
};
</script>

<style scoped>
.page-help-button {
  color: var(--app-neutral-button-text);
}

.page-help-button:hover,
.page-help-button:focus-visible {
  color: rgb(var(--v-theme-primary));
}

.page-help-button__notice {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  padding: 6px 16px 8px;
  color: var(--app-text-default);
  font-size: 0.76rem;
  line-height: 1.35;
  opacity: 0.72;
}

.page-help-button__notice-icon {
  flex-shrink: 0;
  margin-top: 1px;
}
</style>
